/**
 * ConstellationView -- the /twin/constellation page shell.
 *
 * Composes:
 *   * Mode switcher (Observatory / Flow / Orbit)
 *   * MetricsRail (5 click-to-trace metrics)
 *   * Stage: TwinViz SVG + Inspector when a node is selected
 *   * Scrubber (BLAST_RADIUS chain history for the selected node)
 *   * Blast toggle + legend
 *   * ProvenanceTrace overlay (open on any trace click)
 *
 * Data sources:
 *   useTwinGraph  -- full topology
 *   useCascade    -- live impact + rail metrics
 *   useTwinBlast  -- counterfactual reached-set for selected node
 *   useMe         -- admin gate for Verify chain action
 *
 * Empty states:
 *   loading -- spinner banner
 *   error   -- inline error card
 *   empty topology -- "no constellation seeded yet" prompt
 */
import { useAsOf, useAsOfSetter } from "shared/hooks/useAsOf";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "shared/components/EmptyState";
import { useMe } from "shared/api/useMe";
import { Twin3DStage, Twin3DToggle, useTwin3DMode } from "twin/twin3d/Twin3DMode";
import { FullscreenToggle, useFullscreen } from "shared/layout/FullscreenToggle";
import { useTwinBlast, useTwinGraph } from "twin/api/constellation_hooks";
import { useFragility } from "twin/api/fragility_hooks";
import { useTelemetry } from "twin/api/telemetry_hooks";
import { useCascade } from "twin/api/hooks";
import { INSPECTOR_WIDTH_PX, Inspector } from "twin/constellation/Inspector";
import { MetricsRail, type MetricKey } from "twin/constellation/MetricsRail";
import { listRouteForMetric, nodesForMetric } from "twin/constellation/metricNodes";
import { ProvenanceTrace, type TraceCoords } from "twin/constellation/ProvenanceTrace";
import { AsOfBanner } from "shared/components/AsOfBanner";
import { Scrubber } from "twin/constellation/Scrubber";
import { TwinViz, type ConstellationMode } from "twin/constellation/TwinViz";
import { VIEW_H, VIEW_W } from "twin/constellation/layouts";
import { mvbNodeSet } from "twin/constellation/mvbSet";
import { SEARCH_MIN_CHARS, matchNodes } from "twin/constellation/searchMatch";
import {
  buildDependents,
  buildUpstream,
  maxDepth,
  reachableFrom,
  reachableWithDepth,
  sortNodes,
} from "twin/constellation/blastSet";
import { useHopReveal } from "twin/constellation/hopReveal";
import {
  CONSTELLATION_NODE_CAP,
  PINNED_MATCH_LIMIT,
  capNodes,
} from "twin/constellation/nodeCap";
import { buildStateMap } from "twin/constellation/nodeState";
import { sealWord } from "twin/constellation/sealWord";

import "twin/constellation/constellation.css";
import { Link } from "react-router-dom";

const MODES: { id: ConstellationMode; labelKey: string }[] = [
  { id: "observatory", labelKey: "modules.twin.constellation.modes.observatory" },
  { id: "flow", labelKey: "modules.twin.constellation.modes.flow" },
  { id: "orbit", labelKey: "modules.twin.constellation.modes.orbit" },
  { id: "ring", labelKey: "modules.twin.constellation.modes.ring" },
];

export function ConstellationView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  // C4 -- clicking a scrubber tick did nothing visible: `asOf` lived
  // in local state and reached only the provenance overlay, while the
  // canvas kept reading the live graph. A time-travel control that
  // leaves the picture unchanged reads as a broken control, and the
  // one thing worse than not offering time travel is appearing to.
  // In the URL, so the view a person is looking at is one they can
  // share -- which is the whole point of asking "as of when".
  const asOf = useAsOf();
  const setAsOf = useAsOfSetter();
  const graph = useTwinGraph({ as_of: asOf });
  // C4, third round -- the graph moved to the chosen moment and the
  // CASCADE did not, so every node kept its live colour and the
  // picture was identical whichever tick was clicked. The topology
  // rarely changes between two seals; the states are the whole point
  // of asking "as of when".
  const cascade = useCascade(asOf ? { as_of: asOf } : {});

  const [mode, setMode] = useState<ConstellationMode>("observatory");
  // 2D | 3D, persisted per browser so an operator's last choice sticks.
  // Shared with every other Twin tab that offers a 3D landing.
  const {
    effectiveMode,
    setMode: setViewMode,
    onWebglFail,
    loadFailed,
  } = useTwin3DMode("constellation");
  // P385 ring mode: services with no modelled dependency are off the
  // ring by default (37 of 78 carry one on the live estate), but can be
  // admitted so the gap reads as an estate fact rather than a missing
  // half of the picture.
  const [includeIsolated, setIncludeIsolated] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [activeMetric, setActiveMetric] = useState<MetricKey | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [showBlast, setShowBlast] = useState<boolean>(true);
  const [lens, setLens] = useState<
    "none" | "concentration" | "exposure" | "live" | "mvb"
  >("none");
  const [traceCoords, setTraceCoords] = useState<TraceCoords | null>(null);
  const [tooltip, setTooltip] = useState<{
    label: string;
    kind: string;
    /** Round 19 B25 -- the seal's word, or the legacy visual state. */
    state: string;
    x: number;
    y: number;
  } | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const { isFullscreen } = useFullscreen(rootRef);
  const roRef = useRef<ResizeObserver | null>(null);

  // Track the stage's pixel size so the SVG viewBox can match its
  // aspect ratio -- otherwise `preserveAspectRatio` letterboxes the
  // 1240x720 viewBox inside a wider stage, leaving the dead space
  // on the left + right the user flagged. We widen the coordinate
  // space to the stage aspect so the layouts fill it edge to edge.
  //
  // A callback ref (not a mount-time effect): the stage sits behind
  // the loading/error gates, so it isn't in the DOM when an effect
  // would first run. The callback fires when the node actually
  // mounts, and still backs `stageRef` for the tooltip math.
  const [stageSize, setStageSize] = useState<{ w: number; h: number } | null>(null);
  const attachStage = useCallback((el: HTMLDivElement | null) => {
    stageRef.current = el;
    roRef.current?.disconnect();
    if (el && typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver((entries) => {
        const cr = entries[0]?.contentRect;
        if (cr && cr.width > 0 && cr.height > 0) {
          setStageSize({ w: cr.width, h: cr.height });
        }
      });
      ro.observe(el);
      roRef.current = ro;
    }
  }, []);
  const viewW = useMemo(() => {
    if (!stageSize) return VIEW_W;
    const aspect = stageSize.w / stageSize.h;
    // Never narrower than the design width; cap so an ultrawide
    // monitor doesn't stretch the scene to absurdity.
    return Math.round(Math.min(2400, Math.max(VIEW_W, VIEW_H * aspect)));
  }, [stageSize]);

  /* Round 11 C1 -- the canvas the application's own floating queue
     panel is sitting on.
     
     It is pinned over the bottom-start corner of the stage and it ate
     two vendors: a real mouse click at "AWS -- Riyadh region" landed
     on the panel, and the node beneath could not be selected at all.
     The panel belongs to the shell, so the SCENE reserves for it --
     measured from the panel's own rect through the same scale the
     viewBox already uses, and zero when it is collapsed or absent, so
     no scene without one moves. */
  const [panelWidth, setPanelWidth] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined" || typeof ResizeObserver === "undefined") {
      return;
    }
    const panel = document.querySelector('[data-testid="appshell-floating-queue"]');
    if (!panel) return;
    // Observed rather than read once: the panel collapses, and a
    // reserve that does not follow it leaves a hole in the scene for
    // no reason.
    const ro = new ResizeObserver((entries) => {
      setPanelWidth(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(panel);
    setPanelWidth(panel.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  /**
   * Round 12 E4 -- and the OTHER bottom corner, which round 11 argued
   * did not need reserving for. That argument is now falsified by
   * measurement, and is kept here because it was reasonable.
   *
   * It ran: the inspector carries a close control, so a node beneath it
   * is one click from reachable, and reserving for a transient panel
   * would cost a third of the bottom row permanently.
   *
   * What was measured instead, with the inspector OPEN -- which is how
   * any reader who has already clicked one node will have it -- is a
   * real mouse click at every node's own centre: 64 of 78 select
   * themselves, 13 leave the previous selection standing, one is
   * silent, and twelve node centres sit inside the panel's rectangle.
   * The walk found the same shape and the case that settles it:
   * clicking a vendor pressed "Show how this was derived" on SOMEBODY
   * ELSE'S node. That is not a reachability inconvenience the close
   * button answers. It is a wrong action on a different entity, taken
   * by a reader who believes they clicked what was under the pointer.
   *
   * So the scene reserves for it, and reserves ALWAYS rather than only
   * while it is open: a layout that reflows on selection moves the node
   * the reader has just clicked, which is its own defect. The cost
   * round 11 named is real and is paid; what it buys is that the
   * question cannot arise.
   */
  const reserveBottomStart = useMemo(() => {
    if (!stageSize || stageSize.w <= 0 || panelWidth <= 0) return 0;
    // Panel width in viewBox units: the stage's width maps to viewW.
    // Plus a node radius of clearance, so a reserved edge is a gap and
    // not a graze.
    return Math.round((panelWidth / stageSize.w) * viewW) + 24;
  }, [stageSize, viewW, panelWidth]);
  /* The inspector's width is fixed by its own class and is read from a
     constant rather than measured, for the reason above: measuring
     would mean reserving nothing until it first opens, and the reserve
     has to be in place BEFORE the first selection or the first
     selection is the one that moves the scene. Its HEIGHT is bounded to
     half the stage by the same module, which is what makes the two
     bottom bands the right ones to reserve; a test holds the pair. */
  /* Round 12 E4 -- how many nodes the scene drew without naming them.
     Labels that would collide are dropped rather than overprinted, and
     on the live estate that is seventeen of seventy-eight. Saying so is
     the difference between a deliberate choice and a scene that looks
     like it does not know what it is drawing. */
  const [labelsHidden, setLabelsHidden] = useState(0);
  const reserveBottomEnd = useMemo(() => {
    if (!stageSize || stageSize.w <= 0) return 0;
    return Math.round((INSPECTOR_WIDTH_PX / stageSize.w) * viewW) + 24;
  }, [stageSize, viewW]);

  const allNodes = useMemo(
    () => (graph.data ? sortNodes(graph.data.nodes) : []),
    [graph.data],
  );
  const allEdges = useMemo(() => graph.data?.edges ?? [], [graph.data]);
  const states = useMemo(
    () => buildStateMap(allNodes, cascade.data),
    [allNodes, cascade.data],
  );
  /* ROUND 13 C6 -- draw the ones that carry the answer, and say how
     many are not there. At 3,031 nodes a click selected a NEIGHBOUR 174
     times because the orbs stack rather than sit adjacent, and the
     view's own note said 2,840 labels could not be drawn without
     printing over another. The layout answer for three thousand orbs in
     one viewport is not to draw three thousand orbs. */
  // Search: highlight matching nodes on the canvas, dim the rest.
  //
  // Round 14 E1 -- searched over EVERY node the graph returned, and the
  // matches brought into the drawing. The note under the cap says
  // "search for one by name", and every hidden name returned 0 matches
  // because the search looked only at the nodes already drawn.
  const searchActive = query.trim().length >= SEARCH_MIN_CHARS;
  const matches = useMemo(() => matchNodes(allNodes, query), [allNodes, query]);
  const pinned = useMemo(() => {
    const ids = new Set<string>();
    for (const id of matches) {
      if (ids.size >= PINNED_MATCH_LIMIT) break;
      ids.add(id);
    }
    if (selected) ids.add(selected);
    return ids;
  }, [matches, selected]);
  const capped = useMemo(
    () => capNodes(allNodes, allEdges, states, CONSTELLATION_NODE_CAP, pinned),
    [allNodes, allEdges, states, pinned],
  );
  const nodes = capped.nodes;
  const edges = capped.edges;
  // Rail-metric highlight: clicking a metric lights its impacted nodes via
  // the same highlight visual as search. Search wins when both are active.
  const metricHighlight = useMemo(
    () => (activeMetric ? nodesForMetric(activeMetric, cascade.data, nodes) : null),
    [activeMetric, cascade.data, nodes],
  );
  // P369 (F5): the MVB lens reuses the highlight/dim model -- the
  // declared MVB set plus everything it rests on stays lit.
  const mvbSet = useMemo(
    () => (lens === "mvb" ? mvbNodeSet(nodes, edges) : null),
    [lens, nodes, edges],
  );
  const matchSet = searchActive ? matches : (mvbSet ?? metricHighlight);
  /* Round 16 E14 -- and a search that finds exactly one opens it, as
     the 3D scene does. With more than one the highlight stays, because
     a selection takes precedence over it (P93) and would hide the rest. */
  const onlyMatch = searchActive && matches.size === 1 ? [...matches][0]! : null;
  useEffect(() => {
    if (onlyMatch) setSelected(onlyMatch);
  }, [onlyMatch]);

  // P167/P175 fragility lens: a per-node {ratio, stress} map normalised
  // to the top score, so the fragile nodes glow largest. Two separate
  // lenses (P175): "concentration" = upstream SPOFs (watchlist);
  // "exposure" = thinly-protected IBSes (exposed).
  const fragilityData = useFragility(50);
  const fragilityMap = useMemo(() => {
    if ((lens !== "concentration" && lens !== "exposure") || !fragilityData.data)
      return null;
    const entries =
      lens === "concentration"
        ? fragilityData.data.watchlist.map((n) => ({
            id: n.node_id,
            score: n.fragility_score,
            stress: n.open_findings > 0,
          }))
        : fragilityData.data.exposed.map((e) => ({
            id: e.ibs_id,
            score: e.exposure_score,
            stress: e.open_findings > 0,
          }));
    const max = entries.reduce((m, e) => Math.max(m, e.score), 0) || 1;
    const map = new Map<string, { ratio: number; stress: boolean }>();
    for (const e of entries) {
      const ratio = Math.max(0, Math.min(1, e.score / max));
      const prev = map.get(e.id);
      if (!prev || ratio > prev.ratio) {
        map.set(e.id, { ratio, stress: e.stress });
      }
    }
    return map;
  }, [lens, fragilityData.data]);
  // P197 live-telemetry lens: a per-service current-health map; the
  // service nodes get a status ring (down/degraded glow) -- the live board.
  // Poll while the live lens is open so the board stays current.
  const telemetry = useTelemetry(lens === "live");
  const liveHealthMap = useMemo(() => {
    if (lens !== "live" || !telemetry.data) return null;
    const map = new Map<string, "healthy" | "degraded" | "down" | "unknown">();
    for (const row of telemetry.data.rows) {
      // C5 -- a service nothing has ever sampled carries no signal, so
      // it lights nothing. Rendering it as a status would show the
      // live board a fault it has no evidence for.
      if (row.status === "no_data") continue;
      map.set(row.service_id, row.status);
    }
    return map;
  }, [lens, telemetry.data]);
  // P197 follow-on: a small down/degraded tally chip beside the lens
  // switcher so the live board's severity reads at a glance.
  const liveCounts = useMemo(() => {
    let down = 0;
    let degraded = 0;
    if (liveHealthMap) {
      for (const s of liveHealthMap.values()) {
        if (s === "down") down += 1;
        else if (s === "degraded") degraded += 1;
      }
    }
    return { down, degraded };
  }, [liveHealthMap]);
  const dependents = useMemo(() => buildDependents(edges), [edges]);
  const upstreamMap = useMemo(() => buildUpstream(edges), [edges]);
  const blastSet = useMemo(
    () => (selected ? reachableFrom(selected, dependents) : null),
    [selected, dependents],
  );

  // P385: trace BOTH directions from the seed. Downstream is
  // consequence ("if this fails, who is hurt"); upstream is
  // prerequisite ("what must come back before this can"). They are
  // rendered distinctly on purpose.
  const trace = useMemo(
    () =>
      selected
        ? {
            downstream: reachableWithDepth(selected, dependents),
            upstream: reachableWithDepth(selected, upstreamMap),
          }
        : null,
    [selected, dependents, upstreamMap],
  );
  const traceDepth = useMemo(
    () => (trace ? maxDepth(trace.downstream, trace.upstream) : 0),
    [trace],
  );
  const { revealDepth, skip, revealing } = useHopReveal(traceDepth, selected);

  const selectedNode = nodes.find((n) => n.id === selected) ?? null;
  const selectedState = selected ? (states.get(selected) ?? "stable") : "stable";
  // Pre-fetch the selected node's blast graph so the inspector
  // surfaces the reached_total counts; the hook is keyed by id
  // so a no-op when nothing is selected.
  useTwinBlast({
    kind: selectedNode?.kind ?? null,
    id: selected,
  });

  // ── Render ────────────────────────────────────────────────────

  if (graph.isError) {
    return (
      <article className="m-6 rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        <strong className="block font-semibold">
          {t("modules.twin.constellation.error")}
        </strong>
        {graph.error.detail}
      </article>
    );
  }

  if (graph.isPending) {
    return (
      <article
        className="m-6 text-sm text-slate-500"
        data-testid="constellation-loading"
      >
        {t("modules.twin.constellation.loading")}
      </article>
    );
  }

  // Round 14 B5 -- a bare tenant carries the seven seeded channels and
  // nothing else: "7 entities and 0 dependencies" and a row of zeros,
  // with no sentence. Channels alone are not an estate to map.
  const empty =
    nodes.length === 0 || !nodes.some((n) => n.kind === "ibs" || n.kind === "service");
  // The same flag the cascade reads in both modes, so the verdict does
  // not change between scenes.
  const unassessed = cascade.data?.estate_assessed === false;

  return (
    <article
      ref={rootRef}
      data-testid="constellation-view"
      // Full-bleed stage: fill the viewport below the brand header.
      // The header is the glyph-lockup strip (~81px = 5.0625rem), so
      // we subtract 5.25rem -- a hair more than the header -- to clear
      // it without a page scrollbar; the slack hides under the p-3.
      // In fullscreen the brand header is gone, so use the full height.
      className={
        "flex flex-col gap-2 p-3 " +
        (isFullscreen ? "h-screen bg-white" : "h-[calc(100vh-5.25rem)]")
      }
    >
      {/* Compact top strip -- kicker + heading + mode switcher on
       * a single line so the stage gets the full remaining height.
       * The hero header from P83 ate 80px on every render; this
       * shrinks it to ~32px. */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display text-lg text-slate-900">
            {t("modules.twin.constellation.heading")}
          </h1>
          <p className="hidden text-[10px] uppercase tracking-wide text-slate-500 lg:inline">
            {t("modules.twin.constellation.kicker")}
          </p>
          <Twin3DToggle
            mode={effectiveMode}
            onChange={setViewMode}
            testid="constellation-view"
            loadFailed={loadFailed}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            dir="auto"
            data-testid="constellation-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            /* Round 16 E14 -- the flat view marked a match and never
               selected it, where the 3D scene frames and opens the
               first. Enter opens the first match here too. */
            onKeyDown={(e) => {
              if (e.key !== "Enter" || !searchActive) return;
              const first = matches.values().next().value;
              if (first) setSelected(first);
            }}
            placeholder={t("modules.twin.constellation.search.placeholder")}
            className="w-44 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 placeholder:text-slate-400 focus:border-brand-brass focus:outline-none"
          />
          {searchActive && (
            <span
              data-testid="constellation-search-count"
              className="whitespace-nowrap text-[11px] text-slate-500"
            >
              {matches.size > PINNED_MATCH_LIMIT
                ? t("modules.twin.constellation.search.countCapped", {
                    count: matches.size,
                    drawn: PINNED_MATCH_LIMIT,
                  })
                : t("modules.twin.constellation.search.count", {
                    count: matches.size,
                  })}
            </span>
          )}
        </div>
        {/* Round 16 E14 -- the layout and lens buttons are the flat
            view's. Over the 3D scene they stayed offered and redrew
            nothing: eight controls that did not do what they said. */}
        {effectiveMode === "2d" && (
          <>
            <div
              data-testid="constellation-mode-switcher"
              className="inline-flex rounded border border-slate-200 bg-white"
            >
              {MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`constellation-mode-${m.id}`}
                  data-active={mode === m.id ? "true" : "false"}
                  onClick={() => setMode(m.id)}
                  className={
                    "px-3 py-1 text-xs " +
                    (mode === m.id
                      ? "bg-brand-brass text-brand-navy font-medium"
                      : "text-slate-600 hover:bg-slate-50")
                  }
                >
                  {t(m.labelKey)}
                </button>
              ))}
            </div>
            {mode === "ring" && (
              <label
                data-testid="constellation-ring-isolated-toggle"
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600"
                title={t("modules.twin.constellation.ring.isolatedHint")}
              >
                <input
                  type="checkbox"
                  checked={includeIsolated}
                  onChange={(e) => setIncludeIsolated(e.target.checked)}
                  className="h-3 w-3 accent-brand-brass"
                />
                {t("modules.twin.constellation.ring.showIsolated")}
              </label>
            )}
            <div
              data-testid="constellation-lens-switcher"
              className="inline-flex rounded border border-slate-200 bg-white"
              title={t("modules.twin.constellation.lens.hint")}
            >
              {(["concentration", "exposure", "live", "mvb"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  data-testid={`constellation-lens-${mode}`}
                  data-active={lens === mode ? "true" : "false"}
                  onClick={() => setLens((l) => (l === mode ? "none" : mode))}
                  className={
                    "px-3 py-1 text-xs " +
                    (lens === mode
                      ? "bg-brand-brass text-brand-navy font-medium"
                      : "text-slate-600 hover:bg-slate-50")
                  }
                >
                  {t(`modules.twin.constellation.lens.${mode}`)}
                </button>
              ))}
            </div>
            {lens === "mvb" && mvbSet && (
              <span
                data-testid="constellation-mvb-count"
                className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
              >
                {t("modules.twin.constellation.lens.mvbCount", {
                  count: mvbSet.size,
                })}
              </span>
            )}
            {lens === "live" && liveHealthMap && (
              <span
                data-testid="constellation-live-count"
                className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
              >
                {t("modules.twin.constellation.lens.liveCount", {
                  down: liveCounts.down,
                  degraded: liveCounts.degraded,
                })}
              </span>
            )}
          </>
        )}
        {/* Round 10 A1: in 3D the stage paints over this header and
            carries its own exit, so rendering a second identical
            "Exit full screen" 46px away was two buttons for one act.
            The scene that fills the screen owns the way out of it. */}
        {effectiveMode !== "3d" && <FullscreenToggle targetRef={rootRef} />}
      </header>

      {/* D11 -- the scrubber moved the whole picture and said nothing,
          so a reader could be looking at March believing it was today.
          The banner names the moment and offers the way back. */}
      <AsOfBanner asOf={asOf} onReturnToLive={() => setAsOf(null)} />

      {effectiveMode === "3d" && (
        /* ROUND 15 E5 -- the same pins and the first match the flat
           view draws and marks, so "Web" typed here adds the hidden
           channel to the scene, frames it and opens it. */
        <Twin3DStage
          view="constellation"
          onWebglFail={onWebglFail}
          pinnedIds={pinned}
          focusId={searchActive ? (matches.values().next().value ?? null) : null}
        />
      )}

      {effectiveMode === "2d" && (
        <>
          <MetricsRail
            cascade={cascade.data}
            active={activeMetric}
            onMetric={(key) => {
              setQuery("");
              setActiveMetric((cur) => (cur === key ? null : key));
            }}
          />
          {/* Round 9 F2 -- and the rows behind the figure.
              Eighth round of "clicking the metric routes nowhere". The
              highlight stays: it is the canvas's own answer, and it
              was the right fix for the 404 it replaced. What was
              missing is the LIST, which the board's identical figure
              has opened since D2. Shown only once a metric is active,
              and only for metrics whose filter renders exactly what
              the figure counted. */}
          {activeMetric && listRouteForMetric(activeMetric) && (
            <p className="mt-1 text-center text-[11px]">
              <Link
                to={listRouteForMetric(activeMetric)!}
                data-testid="constellation-metric-list"
                className="text-brand-brass hover:underline"
              >
                {t("modules.twin.constellation.metricList")}
              </Link>
            </p>
          )}
        </>
      )}

      {/* Round 19 H56 -- the sentence sat in the drawing's end corner
          (round 15 E7 moved it off the trace legend) and covered the
          Voice channel's label there. Above the drawing, nothing is
          under it. */}
      {effectiveMode === "2d" && unassessed && !empty && (
        <p
          data-testid="constellation-not-assessed"
          className="max-w-3xl rounded bg-black/55 px-3 py-2 text-xs text-brand-cream/80"
        >
          {t("modules.twin.cascade.notAssessedBody")}
        </p>
      )}
      {effectiveMode === "2d" && (
        <div
          ref={attachStage}
          data-testid="constellation-stage"
          className="constellation-stage relative min-h-[420px] flex-1"
          // A reveal in flight is skippable by clicking anywhere: an
          // operator asking "what is broken NOW" should never have to
          // wait out an animation.
          onClickCapture={revealing ? () => skip() : undefined}
        >
          {trace !== null && showBlast && !empty && (
            <div
              data-testid="constellation-trace-legend"
              className="pointer-events-none absolute start-4 top-4 z-10 rounded border border-brand-brass/25 bg-brand-navy/80 px-3 py-2 text-[11px] text-brand-cream/90"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block h-0 w-5 border-t-2 border-brand-brass" />
                {t("modules.twin.constellation.trace.downstream", {
                  count: trace.downstream.size,
                })}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="inline-block h-0 w-5 border-t-2 border-dashed border-brand-cream" />
                {t("modules.twin.constellation.trace.upstream", {
                  count: trace.upstream.size,
                })}
              </div>
              {(() => {
                const cycle = [...trace.upstream.keys()].filter((id) =>
                  trace.downstream.has(id),
                ).length;
                return cycle > 0 ? (
                  <div
                    data-testid="constellation-trace-cycle"
                    className="mt-1 text-brand-cream/70"
                  >
                    {t("modules.twin.constellation.trace.cycle", { count: cycle })}
                  </div>
                ) : null;
              })()}
              {revealing && (
                <div className="mt-1 text-brand-cream/60">
                  {t("modules.twin.constellation.trace.skipHint")}
                </div>
              )}
            </div>
          )}
          {empty ? (
            /* Round 12 A6 -- it named a place without going there, and
               named it to a reader who may not be admitted. The
               importer is admin-only, so the CTA is the administrator's
               and everyone else is told who fills this in. */
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <EmptyState
                testid="constellation-empty"
                tone="dark"
                message={
                  t("modules.twin.constellation.empty") +
                  (isAdmin ? "" : " " + t("common.emptyAskAdmin"))
                }
                cta={
                  isAdmin
                    ? {
                        label: t("modules.twin.constellation.emptyCta"),
                        to: "/admin/data-import",
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <TwinViz
              mode={mode}
              viewW={viewW}
              nodes={nodes}
              edges={edges}
              states={states}
              matchSet={matchSet}
              selected={selected}
              blastSet={blastSet}
              showBlast={showBlast}
              fragility={fragilityMap}
              liveHealth={liveHealthMap}
              trace={trace}
              revealDepth={revealDepth}
              includeIsolated={includeIsolated}
              reserveBottomStart={reserveBottomStart}
              reserveBottomEnd={reserveBottomEnd}
              onLabelBudget={setLabelsHidden}
              onSelect={(id) => setSelected((cur) => (cur === id ? null : id))}
              onHover={(node, ev, st) => {
                const r = stageRef.current?.getBoundingClientRect();
                if (!r) return;
                setTooltip({
                  label: node.name,
                  kind: node.kind,
                  /* Round 19 B25 -- the hover says the seal's word, as
                     the inspector does; it read "stable" over amber. */
                  state:
                    node.resilience !== undefined
                      ? t(
                          `modules.twin.constellation.inspector.seal.${sealWord(node, st)}`,
                        )
                      : st,
                  x: ev.clientX - r.left,
                  y: ev.clientY - r.top,
                });
              }}
              onLeave={() => setTooltip(null)}
            />
          )}

          {selectedNode && (
            <Inspector
              node={selectedNode}
              nodes={nodes}
              edges={edges}
              state={selectedState}
              onClose={() => setSelected(null)}
              onSelect={(id) => setSelected(id)}
              onTraceState={() =>
                setTraceCoords({
                  chain: "resilience-state",
                  entity_kind: chainEntityKind(selectedNode.kind),
                  entity_id: selectedNode.id,
                  as_of: asOf,
                })
              }
              onTraceBlast={() =>
                setTraceCoords({
                  chain: "blast-radius",
                  entity_kind: chainEntityKind(selectedNode.kind),
                  entity_id: selectedNode.id,
                  as_of: asOf,
                })
              }
            />
          )}

          {tooltip && (
            <div
              data-testid="constellation-tooltip"
              className="constellation-tooltip"
              style={{ left: tooltip.x + 12, top: tooltip.y + 12 }}
            >
              <div className="constellation-tooltip-kind">{tooltip.kind}</div>
              <div>
                {tooltip.label} — {tooltip.state}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Round 12 E4 -- what the picture is drawn FROM, and what it is
          not saying. The walk read four different node counts across
          five loads and found nothing on the page stating where the
          figure came from; driven here it is stable, which makes the
          missing sentence the whole of what is left. The estate is
          live, so two readings taken an hour apart can legitimately
          differ -- and a figure that can move without saying it may is
          the shape a reader is right to distrust. */}
      {effectiveMode === "2d" && nodes.length > 0 && (
        <p
          data-testid="constellation-provenance"
          className="text-[11px] text-slate-500"
        >
          {t("modules.twin.constellation.drawnFrom", {
            nodes: nodes.length,
            edges: edges.length,
          })}
          {labelsHidden > 0 && (
            <>
              {" "}
              <span data-testid="constellation-labels-hidden">
                {t("modules.twin.constellation.labelsHidden", { count: labelsHidden })}
              </span>
            </>
          )}
        </p>
      )}
      {/* Its own paragraph, not a clause inside the provenance line:
          two statements glued into one run of text read as one
          sentence to anyone copying it out, and this one says the
          picture is incomplete. */}
      {effectiveMode === "2d" && capped.hidden > 0 && (
        <p
          data-testid="constellation-nodes-capped"
          className="text-[11px] text-rag-amber"
        >
          {t("modules.twin.constellation.nodesCapped", {
            count: capped.hidden,
            shown: nodes.length,
          })}
        </p>
      )}

      {effectiveMode === "2d" && (
        <div className="flex flex-wrap items-center gap-3">
          <Scrubber
            selectedKind={selectedNode?.kind ?? null}
            selectedId={selected}
            asOf={asOf}
            onAsOfChange={setAsOf}
          />
          <label className="inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-xs">
            <input
              type="checkbox"
              data-testid="constellation-blast-toggle"
              checked={showBlast}
              onChange={(e) => setShowBlast(e.target.checked)}
            />
            {t("modules.twin.constellation.blastToggle")}
          </label>
        </div>
      )}

      <ProvenanceTrace
        coords={traceCoords}
        onClose={() => setTraceCoords(null)}
        onDrillInto={(next) => setTraceCoords(next)}
        isAdmin={me.data?.is_admin ?? false}
      />
    </article>
  );
}

/**
 * Translate the constellation API's TwinNodeKind to the chain
 * `entity_kind` vocabulary (which uses `ibs_service` not `ibs`).
 */
function chainEntityKind(kind: string): string {
  if (kind === "ibs") return "ibs_service";
  return kind;
}
